import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { formatPhoneInput } from '../utils/phone';

axios.defaults.withCredentials = true;

const bizDayYmd = () => {
  const now = new Date();
  if (now.getHours() < 1) now.setDate(now.getDate() - 1);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
};
const todayYmd = () => bizDayYmd();
const addMonths = (ymd, months) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setMonth(dt.getMonth() + months);
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd2 = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${mm}-${dd2}`;
};

/**
 * 12 格實體排列（列由上到下）：
 * 右欄：1 → 2 → 3 → 4
 * 中欄：5 → 6 → 7 → 8
 * 左欄：9 → 10 → 11 → 12
 * CSS grid 為「左→右」欄，故列順序為：9,5,1 | 10,6,2 | …
 */
const GRID_ORDER_12 = [9, 5, 1, 10, 6, 2, 11, 7, 3, 12, 8, 4];

/** 後端固定回傳 12 格，依實體位置排序 */
function orderedSlotsForDisplay(slots) {
  if (!Array.isArray(slots) || slots.length === 0) return [];
  const byNum = new Map(slots.map((s) => [s.number, s]));
  return GRID_ORDER_12.map((n) => byNum.get(n)).filter(Boolean);
}

function shortName(name, phone) {
  const n = String(name || '').trim();
  if (n) return n.length > 6 ? `${n.slice(0, 5)}…` : n;
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length >= 4) return `…${digits.slice(-4)}`;
  return '—';
}

function Modal({ open, onClose, title, children, width = 480 }) {
  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.45)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: width,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          padding: 16,
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        {title ? <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 800 }}>{title}</h2> : null}
        {children}
      </div>
    </div>
  );
}

export default function LockerBoardPage() {
  const location = useLocation();
  const panelPath = location.pathname.startsWith('/count') ? '/count/member-panel' : '/admin/member-panel';
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [slotCount, setSlotCount] = useState(0);
  const [slots, setSlots] = useState([]);
  const [outOfRange, setOutOfRange] = useState([]);

  const [rentOpen, setRentOpen] = useState(false);
  const [rentSlot, setRentSlot] = useState(null);
  const [phone, setPhone] = useState('');
  const [lookedMember, setLookedMember] = useState(null);
  const [lookupErr, setLookupErr] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [expiryYmd, setExpiryYmd] = useState(() => addMonths(todayYmd(), 1));
  const [rentBusy, setRentBusy] = useState(false);
  const [rentMsg, setRentMsg] = useState('');
  /** 後端回傳須先確認「過期已退租」時，列出他人過期紀錄 */
  const [pendingVacatedAck, setPendingVacatedAck] = useState(null);
  const [vacatedAckChecked, setVacatedAckChecked] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSlot, setDetailSlot] = useState(null);
  const [detailExpiry, setDetailExpiry] = useState('');
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailMsg, setDetailMsg] = useState('');

  const displaySlots = useMemo(() => orderedSlotsForDisplay(slots), [slots]);

  const loadOverview = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const { data } = await axios.get('/api/admin/lockers/overview');
      if (!data?.ok) {
        setErr(data?.error || '讀取失敗');
        setSlots([]);
        setSlotCount(0);
        setOutOfRange([]);
        return;
      }
      setSlotCount(data.slotCount || 0);
      setSlots(Array.isArray(data.slots) ? data.slots : []);
      setOutOfRange(Array.isArray(data.outOfRange) ? data.outOfRange : []);
    } catch (e) {
      setErr(e?.response?.data?.error || '無法載入置物櫃總覽');
      setSlots([]);
      setSlotCount(0);
      setOutOfRange([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const closeRent = () => {
    setRentOpen(false);
    setRentSlot(null);
    setPhone('');
    setLookedMember(null);
    setLookupErr('');
    setRentMsg('');
    setExpiryYmd(addMonths(todayYmd(), 1));
    setPendingVacatedAck(null);
    setVacatedAckChecked(false);
  };

  const openRent = (slotNumber) => {
    setRentSlot(slotNumber);
    setPhone('');
    setLookedMember(null);
    setLookupErr('');
    setRentMsg('');
    setExpiryYmd(addMonths(todayYmd(), 1));
    setPendingVacatedAck(null);
    setVacatedAckChecked(false);
    setRentOpen(true);
  };

  const doLookup = async () => {
    const raw = String(phone || '').trim();
    if (!raw) {
      setLookupErr('請輸入電話');
      setLookedMember(null);
      return;
    }
    setLookupBusy(true);
    setLookupErr('');
    setLookedMember(null);
    try {
      const encoded = encodeURIComponent(formatPhoneInput(raw));
      const { data } = await axios.get(`/api/admin/member/phone/${encoded}`);
      setLookedMember(data);
    } catch (e) {
      setLookedMember(null);
      setLookupErr(e?.response?.status === 404 ? '查無此會員' : (e?.response?.data?.error || '查詢失敗'));
    } finally {
      setLookupBusy(false);
    }
  };

  const confirmRent = async () => {
    if (!lookedMember?.ID || !rentSlot) return;
    const body = {
      locker_number: String(rentSlot),
      expiry_date: expiryYmd || addMonths(todayYmd(), 1),
    };
    if (Array.isArray(pendingVacatedAck) && pendingVacatedAck.length > 0) {
      if (!vacatedAckChecked) {
        window.alert('請勾選「已確認過期客人已實際退租／櫃位已騰空」後再送出。');
        return;
      }
      body.confirm_expired_previous_vacated = true;
    }
    setRentBusy(true);
    setRentMsg('');
    try {
      const { data } = await axios.post(`/api/admin/member/${lookedMember.ID}/locker`, body);
      if (data?.warning) window.alert(data.warning);
      setPendingVacatedAck(null);
      setVacatedAckChecked(false);
      setRentMsg('已指派');
      await loadOverview();
      setTimeout(() => closeRent(), 500);
    } catch (e) {
      const d = e?.response?.data || {};
      if (d.error === 'locker_expired_needs_ack') {
        setPendingVacatedAck(Array.isArray(d.holders) ? d.holders : []);
        setVacatedAckChecked(false);
        const msg = d.message || '請先確認過期客人已退租';
        setRentMsg('請閱讀下方確認區並勾選後再按「確認租借」。');
        window.alert(msg);
        return;
      }
      const msg = d.message || d.error || '指派失敗';
      setRentMsg(msg);
      window.alert(msg);
    } finally {
      setRentBusy(false);
    }
  };

  const openDetail = (slot) => {
    setDetailSlot(slot);
    const occ = slot?.occupants?.[0];
    const ex = occ?.expiry ? String(occ.expiry).split('T')[0] : '';
    setDetailExpiry(ex || addMonths(todayYmd(), 1));
    setDetailMsg('');
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailSlot(null);
    setDetailMsg('');
    setDetailBusy(false);
  };

  const submitChangeExpiry = async (memberId) => {
    if (!detailSlot?.number || !memberId) return;
    setDetailBusy(true);
    setDetailMsg('');
    try {
      const { data } = await axios.post(`/api/admin/member/${memberId}/locker`, {
        locker_number: String(detailSlot.number),
        expiry_date: detailExpiry || addMonths(todayYmd(), 1),
      });
      if (data?.warning) window.alert(data.warning);
      setDetailMsg('到期日已更新');
      await loadOverview();
    } catch (e) {
      const d = e?.response?.data || {};
      const msg = d.message || d.error || '更新失敗';
      setDetailMsg(msg);
      window.alert(msg);
    } finally {
      setDetailBusy(false);
    }
  };

  const releaseLocker = async (memberId) => {
    if (!memberId) return;
    if (!window.confirm('確定清櫃／退租？')) return;
    setDetailBusy(true);
    setDetailMsg('');
    try {
      await axios.post(`/api/admin/member/${memberId}/locker`, { release: true });
      setDetailMsg('已清櫃');
      await loadOverview();
      setTimeout(() => closeDetail(), 400);
    } catch (e) {
      setDetailMsg(e?.response?.data?.error || '清櫃失敗');
    } finally {
      setDetailBusy(false);
    }
  };

  const goMemberPanel = (memberId) => {
    if (!memberId) return;
    window.open(`${panelPath}?memberId=${encodeURIComponent(String(memberId))}`, '_blank', 'noopener,noreferrer');
  };

  const cellBg = (slot) => {
    if (slot.status === 'vacant') return '#f3f4f6';
    if (slot.status === 'duplicate') return '#fef3c7';
    const o = slot.occupants?.[0];
    if (o?.expired) return '#fee2e2';
    return '#dcfce7';
  };

  return (
    <div style={{ padding: '8px 4px 20px', minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>置物櫃總覽</h1>
          <p style={{ margin: '6px 0 0', color: '#555', fontSize: 13 }}>
            固定 12 格 · 點空格租借 · 點占用格檢視／改到期／清櫃
            <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: '#6b7280' }}>
              版面：右欄 1–4、中欄 5–8、左欄 9–12（由上到下）
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadOverview()}
          disabled={loading}
          style={{
            padding: '8px 16px',
            borderRadius: 10,
            border: '1px solid #ccc',
            background: '#fff',
            fontWeight: 700,
            cursor: loading ? 'wait' : 'pointer',
          }}
        >
          重新整理
        </button>
      </div>

      {err ? (
        <div style={{ padding: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 10, marginBottom: 12 }}>{err}</div>
      ) : null}

      {loading ? <p style={{ color: '#666' }}>載入中…</p> : null}

      {!loading && !err ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(96px, 1fr))',
            gap: 8,
            maxWidth: 440,
          }}
        >
          {displaySlots.map((slot) => {
            const needsAckAny = (slot.occupants || []).some((o) => o.needsVacatedAck);
            return (
            <button
              key={slot.number}
              type="button"
              onClick={() => {
                if (slot.status === 'vacant') openRent(slot.number);
                else openDetail(slot);
              }}
              style={{
                minHeight: 88,
                borderRadius: 10,
                border: slot.status === 'duplicate'
                  ? '2px solid #f59e0b'
                  : needsAckAny
                    ? '2px solid #ea580c'
                    : '1px solid #ddd',
                background: cellBg(slot),
                cursor: 'pointer',
                textAlign: 'left',
                padding: '8px 10px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 900, fontSize: 15 }}>{slot.number} 號</div>
              {slot.status === 'vacant' ? (
                <div style={{ color: '#6b7280' }}>空置</div>
              ) : (
                <>
                  {slot.occupants.map((o) => (
                    <div key={o.memberId} style={{ lineHeight: 1.35 }}>
                      <div style={{ fontWeight: 700 }}>{shortName(o.name, o.phone)}</div>
                      <div style={{ fontSize: 11, color: o.expired ? '#b91c1c' : '#374151' }}>
                        {o.expiry ? String(o.expiry).split('T')[0] : '—'}
                        {o.expired ? ' 已到期' : ''}
                        {o.needsVacatedAck ? (
                          <span style={{ display: 'block', color: '#c2410c', fontWeight: 700 }}>待確認退租</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                  {slot.status === 'duplicate' ? (
                    <div style={{ fontSize: 11, color: '#b45309', fontWeight: 700 }}>資料衝突</div>
                  ) : null}
                </>
              )}
            </button>
          );})}
        </div>
      ) : null}

      {outOfRange.length > 0 ? (
        <div style={{ marginTop: 16, padding: 12, background: '#fff7ed', borderRadius: 10, fontSize: 13 }}>
          <strong>櫃號不在 1～{slotCount} 的紀錄</strong>（請在櫃台會員頁修正）：
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {outOfRange.map((o) => (
              <li key={`${o.memberId}-${o.lockerNote}`}>
                會員 #{o.memberId} {o.name || ''} 櫃號「{o.lockerNote}」
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Modal
        open={rentOpen}
        onClose={() => !rentBusy && closeRent()}
        title={rentSlot != null ? `租借 — ${rentSlot} 號櫃` : '租借'}
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label style={{ fontSize: 13, fontWeight: 700 }}>電話</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+886…"
              style={{ flex: '1 1 200px', padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc', fontSize: 15 }}
            />
            <button
              type="button"
              onClick={() => doLookup()}
              disabled={lookupBusy}
              style={{ padding: '8px 14px', borderRadius: 8, fontWeight: 700, border: '1px solid #333', background: '#111', color: '#fff' }}
            >
              {lookupBusy ? '查詢中…' : '查詢'}
            </button>
          </div>
          {lookupErr ? <div style={{ color: '#b91c1c', fontSize: 13 }}>{lookupErr}</div> : null}

          {lookedMember ? (
            <div style={{ padding: 12, background: '#f9fafb', borderRadius: 10, fontSize: 14 }}>
              <div><strong>姓名</strong> {lookedMember.NAME || '—'}</div>
              <div><strong>ID</strong> {lookedMember.ID}</div>
              <div><strong>電話</strong> {lookedMember.PHONE || '—'}</div>
              <div><strong>等級</strong> {lookedMember.DISPLAY_LEVEL || lookedMember.LEVEL || '—'}</div>
              {lookedMember.lockerNumber ? (
                <div style={{ marginTop: 8, padding: 8, background: '#fef3c7', borderRadius: 8, color: '#92400e' }}>
                  目前已有 <strong>{lookedMember.lockerNumber}</strong> 號櫃；確認後將改綁為 <strong>{rentSlot}</strong> 號櫃（以最新一筆紀錄為準）。
                </div>
              ) : null}
            </div>
          ) : null}

          <label style={{ fontSize: 13, fontWeight: 700 }}>到期日</label>
          <input
            type="date"
            value={expiryYmd}
            onChange={(e) => setExpiryYmd(e.target.value)}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc', maxWidth: 200 }}
          />

          {Array.isArray(pendingVacatedAck) && pendingVacatedAck.length > 0 ? (
            <div style={{ padding: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 13 }}>
              <div style={{ fontWeight: 800, marginBottom: 8, color: '#92400e' }}>過期紀錄須先確認退租</div>
              <p style={{ margin: '0 0 8px', color: '#78350f' }}>
                下列為仍綁在此櫃的過期紀錄。請向客人確認<strong>已實際退租、櫃位已騰空</strong>後再勾選。
              </p>
              <ul style={{ margin: '0 0 10px', paddingLeft: 18 }}>
                {pendingVacatedAck.map((h) => (
                  <li key={h.memberId}>
                    {h.name || `會員#${h.memberId}`}
                    {h.expiry ? `（到期 ${String(h.expiry).split('T')[0]}）` : ''}
                  </li>
                ))}
              </ul>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={vacatedAckChecked}
                  onChange={(e) => setVacatedAckChecked(e.target.checked)}
                />
                <span>我已確認上述過期客人<strong>已完成退租</strong>，置物櫃已騰空，同意建立新租借。</span>
              </label>
            </div>
          ) : null}

          {rentMsg ? <div style={{ fontSize: 13, color: rentMsg.includes('失敗') ? '#b91c1c' : '#15803d' }}>{rentMsg}</div> : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={() => closeRent()} disabled={rentBusy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff' }}>
              取消
            </button>
            <button
              type="button"
              onClick={() => confirmRent()}
              disabled={
                rentBusy
                || !lookedMember?.ID
                || (Array.isArray(pendingVacatedAck) && pendingVacatedAck.length > 0 && !vacatedAckChecked)
              }
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #166534', background: '#15803d', color: '#fff', fontWeight: 800 }}
            >
              {rentBusy ? '處理中…' : '確認租借'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={detailOpen}
        onClose={() => !detailBusy && closeDetail()}
        title={detailSlot ? `${detailSlot.number} 號櫃` : '櫃位'}
        width={520}
      >
        {!detailSlot ? null : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {detailSlot.status === 'duplicate' ? (
              <div style={{ padding: 10, background: '#fef3c7', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                多筆會員同時占用此櫃號，請逐一確認後清櫃或改綁。
              </div>
            ) : null}

            {detailSlot.occupants.map((o) => (
              <div
                key={o.memberId}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 14 }}>
                  <div><strong>姓名</strong> {o.name || '—'}</div>
                  <div><strong>ID</strong> {o.memberId}</div>
                  <div><strong>電話</strong> {o.phone || '—'}</div>
                  <div><strong>等級</strong> {o.displayLevel || '—'}</div>
                  <div>
                    <strong>到期</strong>{' '}
                    <span style={{ color: o.expired ? '#b91c1c' : undefined }}>
                      {o.expiry ? String(o.expiry).split('T')[0] : '—'}
                      {o.expired ? '（已到期）' : ''}
                    </span>
                  </div>
                  {o.needsVacatedAck ? (
                    <div style={{ marginTop: 8, padding: 8, background: '#fff7ed', borderRadius: 8, fontSize: 13, color: '#9a3412', fontWeight: 700 }}>
                      已過期且尚未確認退租：新租客登記前須完成確認流程（清櫃或由新店租借時勾選確認）。
                    </div>
                  ) : null}
                </div>

                {detailSlot.occupants.length === 1 ? (
                  <label style={{ fontSize: 13, fontWeight: 700 }}>
                    變更到期日
                    <input
                      type="date"
                      value={detailExpiry}
                      onChange={(e) => setDetailExpiry(e.target.value)}
                      style={{ display: 'block', marginTop: 6, padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc' }}
                    />
                  </label>
                ) : null}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {detailSlot.occupants.length === 1 ? (
                    <button
                      type="button"
                      disabled={detailBusy}
                      onClick={() => submitChangeExpiry(o.memberId)}
                      style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#eff6ff', fontWeight: 700 }}
                    >
                      儲存新到期日
                    </button>
                  ) : (
                    <>
                      <input
                        type="date"
                        value={detailExpiry}
                        onChange={(e) => setDetailExpiry(e.target.value)}
                        style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid #ccc' }}
                      />
                      <button
                        type="button"
                        disabled={detailBusy}
                        onClick={() => submitChangeExpiry(o.memberId)}
                        style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#eff6ff', fontWeight: 700 }}
                      >
                        為此會員更新到期
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    disabled={detailBusy}
                    onClick={() => releaseLocker(o.memberId)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #b91c1c', background: '#fef2f2', fontWeight: 700, color: '#991b1b' }}
                  >
                    清櫃
                  </button>
                  <button
                    type="button"
                    onClick={() => goMemberPanel(o.memberId)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', fontWeight: 700 }}
                  >
                    櫃台完整頁
                  </button>
                </div>
              </div>
            ))}

            {detailMsg ? <div style={{ fontSize: 13, color: detailMsg.includes('失敗') ? '#b91c1c' : '#15803d' }}>{detailMsg}</div> : null}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => closeDetail()} disabled={detailBusy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff' }}>
                關閉
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
